import React, { useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, LayersControl, AttributionControl, useMap } from "react-leaflet";
import L from "leaflet";
import { useTranslation } from "react-i18next";
import { eurCompact } from "@/lib/format";
import { placeImage } from "@/lib/placeImage";

function placeIcon(count) {
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;display:flex;align-items:center;justify-content:center;
      background:#0B2545;color:#fff;min-width:34px;height:34px;padding:0 8px;border-radius:17px;
      border:2px solid #C5A059;box-shadow:0 2px 10px rgba(0,0,0,.35);font:600 13px sans-serif">${count}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -18],
  });
}

const highlightIcon = L.divIcon({
  className: "",
  html: `<div style="background:#C5A059;width:20px;height:20px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,.45)"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 20],
  popupAnchor: [0, -18],
});

function FitView({ points, focus, highlight }) {
  const map = useMap();
  React.useEffect(() => {
    if (highlight && highlight.lat && highlight.lng) {
      map.setView([highlight.lat, highlight.lng], 14, { animate: true });
      return;
    }
    if (focus && focus.lat && focus.lng) {
      const propPts = (focus.properties || [])
        .filter((p) => p.lat && p.lng)
        .map((p) => [p.lat, p.lng]);
      if (propPts.length >= 2) {
        map.fitBounds([[focus.lat, focus.lng], ...propPts], { padding: [50, 50], maxZoom: 15 });
      } else {
        map.setView([focus.lat, focus.lng], 13);
      }
      return;
    }
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 12);
    } else {
      map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [40, 40], maxZoom: 12 });
    }
  }, [points, focus, highlight, map]);
  return null;
}

export default function PlacesMap({ places = [], height = "560px", onOpenProfile, onViewProperties, focus = null, highlight = null }) {
  const { t } = useTranslation();
  const points = places.filter((p) => p.lat && p.lng);
  const center = points.length ? [points[0].lat, points[0].lng] : [20, 0];

  return (
    <div style={{ height }} className="rounded-sm overflow-hidden border border-slate-200" data-testid="places-map">
      <MapContainer center={center} zoom={points.length ? 6 : 2} style={{ height: "100%", width: "100%" }} scrollWheelZoom attributionControl={false}>
        <AttributionControl prefix={false} />
        <FitView points={points} focus={focus} highlight={highlight} />
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name={t("places.standard", "Standard")}>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name={t("places.satellite", "Satellite")}>
            <TileLayer
              attribution="Tiles &copy; Esri"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
          </LayersControl.BaseLayer>
        </LayersControl>
        {points.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={placeIcon(p.property_count)}>
            <Popup>
              <div className="font-sans w-56" data-testid={`place-popup-${p.id}`}>
                <div className="h-28 w-full rounded-sm overflow-hidden bg-slate-100 mb-2">
                  <img src={placeImage(p)} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="font-semibold text-slate-900 text-sm">{p.name}</div>
                <div className="text-xs text-slate-500">{p.country}</div>
                <div className="grid grid-cols-2 gap-1 mt-2 text-xs">
                  <div><span className="text-slate-400">{t("places.properties", "Properties")}: </span><b>{p.property_count}</b></div>
                  <div><span className="text-slate-400">{t("places.portfolioValue", "Portfolio value")}: </span><b>{eurCompact(p.portfolio_value)}</b></div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2 text-[10px]">
                  <span className="px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700">{t("places.available", "Available")} {p.status_counts?.Available || 0}</span>
                  <span className="px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700">{t("places.reserved", "Reserved")} {p.status_counts?.Reserved || 0}</span>
                  <span className="px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600">{t("places.sold", "Sold")} {p.status_counts?.Sold || 0}</span>
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  {t("places.demand", "Buyer / investor demand")}: <b>{p.demand_buyers}</b> / <b>{p.demand_investors}</b>
                </div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => onViewProperties && onViewProperties(p)} data-testid={`popup-view-properties-${p.id}`}
                    className="text-[#0B2545] text-xs underline">{t("places.viewProperties", "View properties")}</button>
                  <button onClick={() => onOpenProfile && onOpenProfile(p)} data-testid={`popup-open-profile-${p.id}`}
                    className="text-[#C5A059] text-xs underline">{t("places.openProfile", "Open Place Profile")}</button>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
        {highlight && highlight.lat && highlight.lng && (
          <Marker position={[highlight.lat, highlight.lng]} icon={highlightIcon}>
            <Popup>
              <div className="text-sm font-medium text-slate-900" data-testid="places-map-highlight">{highlight.name}</div>
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
}
